/**
 * Constants.ts - Centralized configuration constants for Atmosphere
 * Single Source of Truth for performance profiles, colors, and default config
 */

import type { AtmosphereConfig } from './StateManager';

/**
 * Snow color definition
 */
export interface SnowColor {
  name: string;
  r: number;
  g: number;
  b: number;
}

/**
 * Performance profiles based on benchmark results
 * Single Source of Truth for performance scaling
 */
export const PERFORMANCE_PROFILES = {
  Low: {
    particleCount: 50,
    accumulationRate: 0.4,
    maxAccumulationHeight: 30,
    groundRenderThrottle: 20,  // Render ground every 20 frames
  },
  Mid: {
    particleCount: 40,
    accumulationRate: 0.8,
    maxAccumulationHeight: 45,
    groundRenderThrottle: 12,  // Render ground every 12 frames
  },
  High: {
    particleCount: 60,
    accumulationRate: 1.2,
    maxAccumulationHeight: 60,
    groundRenderThrottle: 8,   // Render ground every 8 frames
  },
} as const;

/**
 * Available snow colors
 */
export const SNOW_COLORS: SnowColor[] = [
  { name: 'Ice Blue', r: 123, g: 166, b: 230 },
  { name: 'White', r: 255, g: 255, b: 255 },
  { name: 'Light Grey', r: 211, g: 211, b: 211 },
];

/**
 * DEFAULT_CONFIG - Strictly derived from Mid performance profile
 * This ensures safe baseline performance for average devices
 */
const MID = PERFORMANCE_PROFILES.Mid;

export const DEFAULT_CONFIG: AtmosphereConfig = {
  enabled: true,
  particleCount: MID.particleCount,
  snow: {
    enabled: true,
    intensity: 0.7,
    speed: {
      min: 5,
      max: 25,
    },
    size: {
      min: 12,
      max: 32,
    },
    wind: {
      enabled: true,
      strength: 0.0,              // Directional wind: -5.0 (left) to 5.0 (right), 0 = no wind
      frequency: 0.002,           // Kept for subtle natural variation
    },
    accumulation: {
      enabled: true,
      maxHeight: MID.maxAccumulationHeight,
      rate: MID.accumulationRate,
      meltRate: 0,
      groundRenderThrottle: MID.groundRenderThrottle,
    },
    color: {
      r: SNOW_COLORS[0].r,        // Ice Blue (default)
      g: SNOW_COLORS[0].g,
      b: SNOW_COLORS[0].b,
      alpha: 0.85,
    },
  },
};

